import { Controller } from '@hotwired/stimulus';

// The caret on the goal region's heading of a progress report's fill page: every goal row open, or
// every one closed, at once. The rows are driven through their own goals-v3-row controllers, which
// apply the state synchronously (no animation), so a page-wide sweep can never be half-dropped by
// Bootstrap's in-transition guard. Opening leaves the rows the goal picker hides alone (they would
// otherwise reappear already open); closing sweeps every row.
//
// The caret reads as open while every shown row is, and follows the rows when they are opened or
// closed one at a time (the plugin's shown/hidden events, jQuery-triggered, so bound with jQuery;
// without jQuery, on its own clicks only). Mounted on the element that wraps the heading and the
// goal cards; the rows' state is read from their markup, since their controllers connect after
// this one.
//
// Targets:
//   caret - the button (aria-expanded, and is-open on its icon)
export default class extends Controller {
  static targets = ['caret'];

  connect() {
    this.onCollapseEvent = (event) => {
      if (!event.target.classList || !event.target.classList.contains('gv3-expand-detail')) return;
      this.sync();
    };
    const $ = this.jquery();
    if ($) $(this.element).on('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
    this.sync();
  }

  disconnect() {
    const $ = this.jquery();
    if ($) $(this.element).off('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
  }

  toggle() {
    if (this.element.querySelector('.gv3-expand-detail.collapsing')) return;
    const open = this.cards(false).some((card) => !this.isOpen(card));
    this.cards(!open).forEach((card) => {
      const row = this.application.getControllerForElementAndIdentifier(card, 'goals-v3-row');
      if (row) row.applyOpen(open);
    });
    this.sync();
  }

  // Read from the rows themselves rather than their controllers, which connect after this one.
  sync() {
    const shown = this.cards(false);
    const open = shown.length > 0 && shown.every((card) => this.isOpen(card));
    this.caretTargets.forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.title = open ? 'Close every goal' : 'Open every goal';
      const icon = button.querySelector('.gv3-expand-caret');
      if (icon) icon.classList.toggle('is-open', open);
    });
  }

  // Every goal card under this element; when opening, only those on show (a card the goal picker
  // hides carries the hidden attribute).
  cards(includeHidden) {
    return Array.from(this.element.querySelectorAll('[data-controller~="goals-v3-row"]')).filter(
      (element) => includeHidden || !element.hidden
    );
  }

  isOpen(card) {
    const detail = card.querySelector('[data-goals-v3-row-target~="detail"]');
    return Boolean(detail && detail.classList.contains('show'));
  }

  jquery() {
    return typeof window !== 'undefined' && typeof window.$ === 'function' ? window.$ : null;
  }
}
